import React, { createContext, useContext, useState, useEffect } from 'react';
import {
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signOut,
    onAuthStateChanged,
    sendPasswordResetEmail,
    updateProfile,
    GoogleAuthProvider,
    signInWithPopup
} from 'firebase/auth';
import { auth } from '../config/firebase';
import { createUserProfile } from '../services/userService';
import { logLogin, logRegister, logLogout } from '../services/analytics';

// Create the authentication context
const AuthContext = createContext({});

// Custom hook to use the auth context
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

// AuthProvider component to wrap the app
export const AuthProvider = ({ children }) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Sign up function
    const signup = async (email, password, displayName = '') => {
        try {
            setError(null);
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);

            // Update profile with display name if provided
            if (displayName) {
                await updateProfile(userCredential.user, {
                    displayName: displayName
                });
            }

            // Force-refresh token so Firestore has auth credentials before the profile write
            await userCredential.user.getIdToken(true);

            // Create user profile in Firestore for lookups
            await createUserProfile(userCredential.user.uid, {
                email: email,
                displayName: displayName,
                createdAt: new Date()
            });

            logRegister('email');
            return userCredential.user;
        } catch (err) {
            setError(err.message);
            throw err;
        }
    };

    // Login function
    const login = async (email, password) => {
        try {
            setError(null);
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            logLogin('email');
            return userCredential.user;
        } catch (err) {
            setError(err.message);
            throw err;
        }
    };

    // Logout function
    const logout = async () => {
        try {
            setError(null);
            logLogout();
            await signOut(auth);
        } catch (err) {
            setError(err.message);
            throw err;
        }
    };

    // Password reset function
    const resetPassword = async (email) => {
        try {
            setError(null);
            await sendPasswordResetEmail(auth, email);
        } catch (err) {
            setError(err.message);
            throw err;
        }
    };

    // Google Sign-In function
    const loginWithGoogle = async () => {
        try {
            setError(null);
            const provider = new GoogleAuthProvider();
            const userCredential = await signInWithPopup(auth, provider);

            // Force-refresh the ID token so Firestore has valid auth credentials
            // before any subsequent Firestore operations are made. Without this,
            // the SDK may send requests before the token is attached, causing
            // permission-denied errors immediately after sign-in.
            await userCredential.user.getIdToken(true);

            // Create user profile in Firestore for new Google users
            await createUserProfile(userCredential.user.uid, {
                email: userCredential.user.email,
                displayName: userCredential.user.displayName || '',
                photoURL: userCredential.user.photoURL || '',
                createdAt: new Date()
            });

            logLogin('google');
            return userCredential.user;
        } catch (err) {
            setError(err.message);
            throw err;
        }
    };

    // Update user profile
    const updateUserProfile = async (updates) => {
        try {
            setError(null);
            if (currentUser) {
                await updateProfile(currentUser, updates);
                // Trigger a state update
                setCurrentUser({ ...currentUser, ...updates });
            }
        } catch (err) {
            setError(err.message);
            throw err;
        }
    };

    // Listen for auth state changes
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            setCurrentUser(user);
            setLoading(false);
        });

        // Cleanup subscription on unmount
        return unsubscribe;
    }, []);

    const value = {
        currentUser,
        loading,
        error,
        signup,
        login,
        logout,
        resetPassword,
        loginWithGoogle,
        updateUserProfile
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
};

export default AuthContext;